<template>
    <modal
        :show.sync="showModal"
        body-classes="p-0"
        :modal-classes="`modal-xl ${$style.modalClasses}`"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">Request Quote</h2>
            </template>
            <div :class="$style.header">
                <div :class="$style.customer">
                    <div :class="$style.label">Customer</div>
                    <div v-if="allowCustomerSelect">
                        <el-tooltip
                            manual
                            effect="light"
                            :value="missingCustomer"
                        >
                            <el-select
                                v-model="selectedCustomerID"
                                class="select-primary"
                                filterable
                                remote
                                :remote-method="searchCustomers"
                                :loading="loadingCustomerResults"
                                placeholder="Select customer..."
                            >
                                <el-option
                                    v-for="customer in customers"
                                    :key="customer.id"
                                    class="select-primary"
                                    :label="customer.name"
                                    :value="customer.id"
                                />
                            </el-select>
                            <label slot="content" class="error"
                                >Please select a customer.</label
                            >
                        </el-tooltip>
                    </div>
                    <div v-else :class="$style.title">
                        {{ customerName }}
                    </div>
                </div>
                <div :class="$style.designer">
                    <div :class="$style.label">Designer</div>
                    <div :class="$style.title">
                        {{ designerName || "--" }}
                    </div>
                </div>
            </div>
            <el-divider content-position="left">Details</el-divider>
            <ValidationObserver v-slot="{ invalid, validate, pristine }">
                <form
                    class="mt-3"
                    autocomplete="off"
                    @submit.prevent="validate().then(requestQuote)"
                >
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Estimated Receipt Date"
                                rules="required"
                            >
                                <base-input
                                    name="estimatedReceiptDate"
                                    type="date"
                                    label="Estimated Receipt Date"
                                    :error="errors[0]"
                                >
                                    <el-date-picker
                                        v-model="quote.estimatedReceiptDate"
                                        :picker-options="{
                                            disabledDate:
                                                isDateSelectionInvalid,
                                        }"
                                        format="MM/dd/yyyy"
                                        type="date"
                                        placeholder="Select date"
                                    />
                                </base-input>
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Estimated Delivery Date"
                                rules="required"
                            >
                                <base-input
                                    name="estimatedDeliveryDate"
                                    type="date"
                                    label="Estimated Delivery Date"
                                    :error="errors[0]"
                                >
                                    <el-date-picker
                                        v-model="quote.estimatedDeliveryDate"
                                        :picker-options="{
                                            disabledDate:
                                                isDateSelectionInvalid,
                                        }"
                                        format="MM/dd/yyyy"
                                        type="date"
                                        placeholder="Select date"
                                    />
                                </base-input>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-3">
                            <base-input
                                v-model="quote.locationName"
                                name="location"
                                type="text"
                                label="Location Name"
                            />
                        </div>
                        <div class="col-3">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Address"
                                rules="required"
                            >
                                <base-input
                                    v-model="quote.deliveryAddress"
                                    name="address"
                                    type="text"
                                    label="Delivery Address"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-3">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery City"
                                rules="required"
                            >
                                <base-input
                                    v-model="quote.deliveryCity"
                                    name="city"
                                    type="text"
                                    label="Delivery City"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-3">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Zip"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>Delivery Zip</label>
                                    <masked-input
                                        v-model="quote.deliveryZip"
                                        class="form-control"
                                        name="zip"
                                        autocomplete="off"
                                        type="text"
                                        :mask="zipMask"
                                        :guide="true"
                                        placeholder-char="#"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Contact Name"
                                rules="required"
                            >
                                <base-input
                                    v-model="quote.contact"
                                    name="contactName"
                                    type="text"
                                    label="Contact Name"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Contact Phone"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>Contact Phone</label>
                                    <masked-input
                                        v-model="quote.phone"
                                        class="form-control"
                                        name="contactPhone"
                                        autocomplete="off"
                                        type="tel"
                                        :mask="phoneMask"
                                        :guide="true"
                                        placeholder="(123) 456-7890"
                                        :placeholder-char="'\u2000'"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Contact Email"
                                rules="required"
                            >
                                <base-input
                                    v-model="quote.email"
                                    name="contactEmail"
                                    type="text"
                                    label="Contact Email"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6 col-xl-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Deliver Into"
                                rules="required"
                            >
                                <label>Deliver Into</label>
                                <div class="form-group">
                                    <el-radio-group v-model="quote.deliverInto">
                                        <el-radio-button label="House" />
                                        <el-radio-button label="Garage" />
                                        <el-radio-button label="Other" />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-2">
                            <label>Elevator?</label>
                            <div class="form-group">
                                <el-switch
                                    v-model="quote.hasElevator"
                                    active-text="Yes"
                                    inactive-text="No"
                                />
                            </div>
                        </div>
                        <div class="col-3">
                            <label># of Stair Flights</label>
                            <el-input-number
                                v-model="quote.stairFlightCount"
                                :min="0"
                            />
                        </div>
                    </div>
                    <div>
                        <base-input
                            v-if="showNotesTextArea"
                            name="quoteNotes"
                            label="Notes"
                        >
                            <textarea
                                v-model="quote.notes"
                                v-focus
                                style="max-height: unset"
                                class="form-control"
                                rows="3"
                            />
                        </base-input>
                        <base-button
                            v-if="!showNotesTextArea"
                            class="pl-0"
                            link
                            type="primary"
                            @click="showNotesTextArea = true"
                        >
                            Add Notes
                        </base-button>
                    </div>
                    <div class="mt-5">
                        <div :class="$style.sectionTitle">Materials</div>
                        <el-table
                            max-height="350"
                            :data="quote.materials"
                            empty-text="Select a receipt log or add a new material below"
                        >
                            <el-table-column
                                v-for="column in tableColumns"
                                :key="column.label"
                                :width="column.width"
                                :min-width="column.minWidth"
                                :prop="column.prop"
                                :label="column.label"
                                :formatter="column.formatter"
                                :show-overflow-tooltip="true"
                            />
                            <el-table-column
                                min-width="100"
                                header-align="right"
                                label="Actions"
                            >
                                <div slot-scope="{ $index }" class="text-right">
                                    <el-tooltip
                                        content="Remove material"
                                        :open-delay="800"
                                        placement="top"
                                    >
                                        <base-button
                                            type="primary"
                                            size="sm"
                                            icon
                                            @click="removeMaterial($index)"
                                        >
                                            <i class="fa fa-trash-alt" />
                                        </base-button>
                                    </el-tooltip>
                                </div>
                            </el-table-column>
                        </el-table>
                        <form
                            ref="addMaterial"
                            class="mt-3"
                            autocomplete="off"
                            @submit.prevent="addMaterial"
                        >
                            <div class="row">
                                <div class="col-3 flex-between">
                                    <div>
                                        <label class="d-block">
                                            Receipt Log
                                        </label>
                                        <el-select
                                            v-model="material.receiptLogID"
                                            class="select-primary"
                                            clearable
                                            filterable
                                            remote
                                            :remote-method="searchReceiptLogs"
                                            :loading="loadingReceiptLogResults"
                                            placeholder="Search..."
                                        >
                                            <el-option
                                                v-for="log in receiptLogs"
                                                :key="log.id"
                                                class="select-primary"
                                                :label="log.id"
                                                :value="log.id"
                                            >
                                                <div
                                                    :class="
                                                        $style.receiptLogResultOption
                                                    "
                                                >
                                                    <div
                                                        :class="
                                                            $style.flexBetween
                                                        "
                                                    >
                                                        <strong
                                                            >#{{
                                                                log.id
                                                            }}</strong
                                                        >
                                                        <span>{{
                                                            formatDate(log.date)
                                                        }}</span>
                                                    </div>
                                                    <div
                                                        :class="
                                                            $style.flexBetween
                                                        "
                                                    >
                                                        <div class="mr-4">
                                                            {{
                                                                log.manufacturer
                                                                    .name
                                                            }}
                                                        </div>
                                                        <div
                                                            :class="
                                                                $style.customerJobId
                                                            "
                                                            :title="
                                                                log.customerJobId
                                                            "
                                                        >
                                                            {{
                                                                log.customerJobId
                                                            }}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        {{
                                                            log.materialType
                                                                .description
                                                        }}
                                                        {{ log.size }}
                                                        {{ log.sizeType }}
                                                    </div>
                                                </div>
                                            </el-option>
                                        </el-select>
                                    </div>
                                    <div :class="$style.materialOr">OR</div>
                                </div>
                                <div
                                    class="col-9 flex-between"
                                    :class="$style.addMaterialSection"
                                >
                                    <div>
                                        <label class="d-block"
                                            >Material Type</label
                                        >
                                        <el-tooltip
                                            manual
                                            effect="light"
                                            :value="missingMaterialType"
                                        >
                                            <el-select
                                                v-model="
                                                    material.materialTypeID
                                                "
                                                class="select-primary"
                                                filterable
                                                placeholder="Select material type"
                                                :disabled="
                                                    materialInputsDisabled
                                                "
                                            >
                                                <el-option
                                                    v-for="type in materialTypes"
                                                    :key="type.id"
                                                    class="select-primary"
                                                    :label="type.description"
                                                    :value="type.id"
                                                />
                                            </el-select>
                                            <label slot="content" class="error"
                                                >Please select material
                                                type.</label
                                            >
                                        </el-tooltip>
                                    </div>
                                    <div>
                                        <label class="d-block">Quantity</label>
                                        <el-input-number
                                            v-model="material.unitCount"
                                            :min="1"
                                            :disabled="materialInputsDisabled"
                                        />
                                    </div>
                                    <div>
                                        <label class="d-block">Size Type</label>
                                        <el-tooltip
                                            manual
                                            effect="light"
                                            :value="missingSizeType"
                                        >
                                            <el-select
                                                v-model="material.unitSize"
                                                class="select-primary"
                                                placeholder="Select size type"
                                                :disabled="
                                                    materialInputsDisabled
                                                "
                                            >
                                                <el-option
                                                    v-for="type in materialSizeTypes"
                                                    :key="type"
                                                    class="select-primary"
                                                    :label="type"
                                                    :value="type"
                                                />
                                            </el-select>
                                            <label slot="content" class="error"
                                                >Please select size type.</label
                                            >
                                        </el-tooltip>
                                    </div>
                                    <div class="d-flex">
                                        <el-tooltip
                                            placement="top"
                                            manual
                                            effect="light"
                                            :value="missingMaterials"
                                        >
                                            <base-button
                                                id="addMaterial"
                                                :class="
                                                    $style.addMaterialButton
                                                "
                                                native-type="submit"
                                                type="info"
                                                class="btn-fill"
                                            >
                                                Add Material
                                            </base-button>
                                            <label slot="content" class="error">
                                                You must add at least one
                                                material.
                                            </label>
                                        </el-tooltip>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <el-divider />
                    <div :class="$style.actions">
                        <base-button
                            type="default"
                            class="btn-fill"
                            @click="showModal = false"
                        >
                            Cancel
                        </base-button>
                        <div :class="$style.right">
                            <base-button
                                id="requestQuote"
                                :disabled="invalid || pristine"
                                native-type="submit"
                                type="primary"
                                class="btn-fill"
                            >
                                Submit
                            </base-button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </card>
    </modal>
</template>

<script>
import moment from "moment-timezone";
import MaskedInput from "vue-text-mask";
import { phoneMask, zipMask } from "src/helpers/inputMasks";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Modal } from "src/components/UI";
import {
    DatePicker,
    Switch,
    InputNumber,
    RadioGroup,
    RadioButton,
    Checkbox,
    Divider,
} from "element-ui";

export default {
    name: "NewQuoteRequest",
    components: {
        Modal,
        MaskedInput,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
        [InputNumber.name]: InputNumber,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Checkbox.name]: Checkbox,
        [Divider.name]: Divider,
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        materialTypes: {
            type: Array,
            required: true,
        },
        materialSizeTypes: {
            type: Array,
            required: true,
        },
        receiptLogs: {
            type: Array,
            required: true,
        },
        customers: {
            type: Array,
            required: true,
        },
        customerName: {
            type: String,
            default: null,
        },
        designerName: {
            type: String,
            default: null,
        },
        show: {
            type: Boolean,
            required: true,
        },
        loadingReceiptLogResults: {
            type: Boolean,
            required: true,
        },
        loadingCustomerResults: {
            type: Boolean,
            required: true,
        },
        allowCustomerSelect: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            // Save the receipt logs that are used in our materials table
            // because the receiptLogs array may change and we need to be able
            // to display the data from the selected receipt logs
            savedReceiptLogs: [],
            material: {},
            emptyMaterial: { unitCount: 1 },
            quote: {},
            selectedCustomerID: null,
            missingMaterialType: false,
            missingSizeType: false,
            missingMaterials: false,
            missingCustomer: false,
            emptyQuote: {
                materials: [],
                hasElevator: false,
                stairFlightCount: 0,
            },
            phoneMask,
            zipMask,
            selectedDeliverInto: null,
            selectedElevator: null,
            showNotesTextArea: false,
            showModal: false,
            tableColumns: [
                {
                    prop: "receiptLog.id",
                    label: "Receipt No.",
                    formatter: (row) => {
                        const log = this.savedReceiptLogs.find(
                            (l) => l.id === row.receiptLogID,
                        );
                        if (log) return log.id;
                        return null;
                    },
                },
                {
                    prop: "receiptLog.customerJobId",
                    label: "Job ID",
                    formatter: (row) => {
                        const log = this.savedReceiptLogs.find(
                            (l) => l.id === row.receiptLogID,
                        );
                        if (log) return log.customerJobId;
                        return null;
                    },
                },
                {
                    prop: "materialType.description",
                    label: "Description",
                    formatter: (row) =>
                        this.materialTypes.find(
                            (t) => t.id === row.materialTypeID,
                        ).description,
                },
                {
                    prop: "unitCount",
                    label: "Qty",
                },
                {
                    prop: "unitSize",
                    label: "Type",
                },
            ],
        };
    },
    computed: {
        materialInputsDisabled() {
            return this.material.receiptLogID !== undefined;
        },
    },
    watch: {
        show(val) {
            this.showModal = val;
        },
        showModal(val) {
            this.$emit("update:show", val);
        },
        "quote.materials": {
            handler(materials) {
                if (materials.length > 0) this.missingMaterials = false;
            },
        },
        "material.receiptLogID": {
            handler(logId) {
                const log = this.receiptLogs.find(
                    (receipt) => receipt.id === logId,
                );
                if (log) {
                    this.material.unitCount = log.size;
                    this.material.unitSize = log.sizeType;
                    this.material.materialTypeID = log.materialType.id;
                } else {
                    this.material = { ...this.emptyMaterial };
                }
            },
        },
        "material.materialTypeID": {
            handler(val) {
                if (val) {
                    this.missingMaterialType = false;
                }
            },
        },
        "material.unitSize": {
            handler(val) {
                if (val) {
                    this.missingSizeType = false;
                }
            },
        },
        selectedCustomerID(val) {
            if (val) this.missingCustomer = false;
            this.$emit("searchReceiptLogs", "", this.selectedCustomerID);
        },
    },
    created() {
        this.quote = { ...this.emptyQuote };
        this.showModal = this.show;
        this.material = { ...this.emptyMaterial };
    },
    methods: {
        isDateSelectionInvalid(date) {
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            return today > date;
        },
        removeMaterial(index) {
            this.quote.materials.splice(index, 1);
        },
        addMaterial() {
            const isValid = this.validateMaterial();
            if (!isValid) return;

            if (this.material.receiptLogID) {
                const log = this.receiptLogs.find(
                    (l) => l.id === this.material.receiptLogID,
                );
                this.savedReceiptLogs.push(log);
            }
            this.quote.materials.push(this.material);
            this.material = { ...this.emptyMaterial };
            // remove duplicate receipt log ids
            this.quote.materials = this.quote.materials
                // eslint-disable-next-line arrow-body-style
                .filter((material, index, self) => {
                    return (
                        index ===
                        self.findIndex(
                            (m) =>
                                m.receiptLogID === material.receiptLogID &&
                                m.materialTypeID === material.materialTypeID,
                        )
                    );
                });
        },
        validateMaterial() {
            let valid = true;
            if (!this.material.materialTypeID) {
                this.missingMaterialType = true;
                valid = false;
            }
            if (!this.material.unitSize) {
                this.missingSizeType = true;
                valid = false;
            }
            return valid;
        },
        requestQuote() {
            // remove timezone info
            this.quote.estimatedReceiptDate =
                this.quote.estimatedReceiptDate.toDateString();
            this.quote.estimatedDeliveryDate =
                this.quote.estimatedDeliveryDate.toDateString();
            if (this.quote.materials.length === 0) {
                this.missingMaterials = true;
            }
            if (this.allowCustomerSelect && !this.selectedCustomerID) {
                this.missingCustomer = true;
            }
            if (!this.missingMaterials && !this.missingCustomer) {
                this.$emit("addQuote", {
                    ...this.quote,
                    ...(this.selectedCustomerID && {
                        customerID: this.selectedCustomerID,
                    }),
                    stairFlightCount:
                        parseInt(this.quote.stairFlightCount, 10) || 0,
                });
            }
        },
        searchReceiptLogs(search) {
            this.$emit("searchReceiptLogs", search, this.selectedCustomerID);
        },
        searchCustomers(search) {
            this.$emit("searchCustomers", search);
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "";
        },
    },
};
</script>

<style module lang="scss">
@import "assets/sass/dashboard/custom/_variables.scss";
.modalClasses {
    min-width: 750px;
}
.header {
    display: flex;
    padding-left: 10px;
    padding-right: 30px;
    .title {
        font-size: 18px;
        color: $black;
        font-weight: 600;
    }
    .customer {
        flex-basis: 70%;
    }
}
.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}
.actions .right > * {
    margin-right: 10px;
    margin-left: 10px;
}
.checkbox {
    align-self: center;
    margin: 0 !important;
}
.link {
    font-weight: 400;
    padding: 0px;
    margin: 0px;
    margin-left: 3px;
    line-height: 1.5;
}
.sectionTitle {
    font-size: 18px;
    font-weight: 500;
    color: #303133;
}
.addMaterialButton {
    align-self: flex-end;
    margin: 0px;
    width: 172px;
}
.receiptLogResultOption {
    display: flex;
    flex-direction: column;
    max-width: 400px;
}
.flexBetween {
    display: flex;
    justify-content: space-between;
}
.customerJobId {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}
.addMaterialSection > * {
    padding-right: 12px;
}

.materialOr {
    margin-top: 30px;
    margin-left: 20px;
}
</style>

<style>
.el-select-dropdown__item {
    height: auto !important;
}
.el-tooltip__popper label {
    margin-bottom: 0px;
}
</style>
